// client/src/hooks/useOwnedUnits.ts
// 868mbzt38 — D1. `useOwnedUnits` is the single source of the query that
// drives the unit guard. The provider reuses this hook so every consumer
// (the guard, the unit-detail aggregator, future hooks) shares TanStack
// cache by `ownerUnitsQueryKey`. `staleTime` matches the provider's old
// 5-minute window; `retryOnMount: false` keeps the contract's invariant
// — once the query is in error with no data, a new observer does NOT
// trigger a fresh fetch, which is how the guard prevents the cycle.

import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import type { OwnedUnitsResponse } from '@shared/types/owner';

export const ownerUnitsQueryKey = ['owner-portal', 'owned-units'] as const;

export function useOwnedUnits() {
  return useQuery<OwnedUnitsResponse>({
    queryKey: ownerUnitsQueryKey,
    queryFn: () => apiRequest<OwnedUnitsResponse>('/api/owner-portal/owned-units'),
    staleTime: 5 * 60_000,
    // D1: without this, a new observer attaching after the query settled
    // in `status: 'error'` with no data would trigger a fresh network
    // round-trip — the exact mechanism that re-mounts the portal.
    retryOnMount: false,
  });
}
